import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const PRINCIPLES = [
  { title: "Curated, Not Crowded", text: "We focus on products worth gifting rather than overwhelming clients with endless choices." },
  { title: "Corporate First", text: "Every part of the experience is designed around bulk corporate requirements." },
  { title: "Trusted Brands", text: "Premium products from established brands — names your recipients already respect." },
  { title: "Flexible Curation", text: "Gift solutions built around budget, occasion and recipient." },
  { title: "End-to-End Support", text: "From product selection to fulfilment, you work with one team." },
];

export default function About() {
  useEffect(() => {
    document.title = "About — THE GOOD GRID";
  }, []);

  return (
    <div data-testid="about-page" className="bg-charcoal text-ivory min-h-screen pt-28 lg:pt-36 pb-24">
      <div className="px-4 sm:px-8 lg:px-16 max-w-5xl">
        <Reveal><Eyebrow>About</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight leading-[1.1]">
            A premium corporate gifting partner — <span className="italic text-champagne-light">thoughtful products, trusted brands, effortless bulk gifting.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-ivory/55 mt-8 max-w-2xl leading-relaxed text-base sm:text-lg">
            THE GOOD GRID exists for one purpose: corporate gifting, done properly. We work with HR teams, founders, admin and procurement, event agencies and engagement leads to turn gifting from a procurement headache into a considered gesture.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="text-ivory/55 mt-5 max-w-2xl leading-relaxed text-base sm:text-lg">
            We are design-conscious, service-oriented and quietly obsessive about the details — the weight of a box, the finish of a glass, the card inside. No noise, no clutter. Just gifts that mean business.
          </p>
        </Reveal>

        <div className="mt-20 divide-y divide-white/10 border-t border-b border-white/10">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.05}>
              <div className="py-8 grid grid-cols-12 gap-6 items-baseline">
                <span className="col-span-2 sm:col-span-1 font-mono text-xs text-champagne tracking-[0.2em]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="col-span-10 sm:col-span-4 font-serif text-xl sm:text-2xl">{p.title}</h3>
                <p className="col-span-12 sm:col-span-7 text-sm text-ivory/45 leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-16">
          <Link to="/request-quote" data-testid="about-request-quote" className="group inline-flex items-center gap-2 bg-champagne text-charcoal text-[11px] font-mono uppercase tracking-[0.22em] px-7 py-4 hover:bg-champagne-light transition-colors duration-300">
            Work With Us <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </Reveal>
      </div>
    </div>
  );
}
